import {
  CommitmentRequest,
  CommitmentResponse,
  CXoneClient,
} from '@nice-ccf/agent-sdk';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { AgentSettings, Logger } from '@nice-ccf/core-sdk';
import { getUtcTimezoneOffset } from '../../util/common';
import {
  CommitmentEvent,
  ContactCard,
  MediaType,
  PermissionKeys,
  PermissionValues,
  VoiceContactStatus,
} from '@nice-ccf/core-sdk';

export const CCF_COMMITMENT_FEATURE_KEY = 'ccfCommitment';

export const FETCH_TIME_ZONES = 'commitment/fetchTimeZones';
export const FETCH_COMMITMENT = 'commitment/fetchCommitment';
export const CREATE_COMMITMENT = 'commitment/createCommitment';
export const EDIT_COMMITMENT = 'commitment/editCommitment';
export const DELETE_COMMITMENT = 'commitment/deleteCommitment';
export const MAKE_COMMITMENT_CALL = 'commitment/makeCommitmentCall';
export const RESCHEDULE_COMMITMENT = 'commitment/rescheduleCommitment';
export const CANCEL_COMMITMENT = 'commitment/cancelCommitment';
export const CHECK_CREATE_COMMITMENT_PERMISSION =
  'commitment/checkCreateCommitmentPermission';
export const REMOVE_COMMITMENT_SETTINGS = 'commitment/removeCommitmentSettings';

let logger: Logger;

/**
 * @remarks "ScheduledCommitment" is to hold scheduled commitment data
 */
export interface ScheduledCommitment extends ContactCard {
  /**
   * @remarks "firstName" is to hold cutomer's first name
   */
  firstName: string;
  /**
   * @remarks "lastName" is to hold cutomer's last name
   */
  lastName: string;
  /**
   * @remarks "notes" is to hold scheduled commitment notes data
   */
  notes?: string;
  /**
   * @remarks "isCommitmentReminder" is to differencialte normal contact card
   * and scheduled commitment card
   */
  isCommitmentReminder: boolean;
  /**
   * @remarks "target type" is to differentiate type of commitment
   * A for agent and S for skill
   */
  targetType: string;
}
/**
 * @remarks "CommitmentFormState" is for commitment form data
 */
export interface CommitmentFormState {
  /**
   * @remarks "A" if targeted to an Agent or "S" if targeted to a skill
   */
  commitmentType: string;
  /**
   * @remarks Outbound Skill Id to be used for callback
   */
  skillId: string;
  /**
   * @remarks First name of Agent callback is targeted to
   */
  fname: string;
  /**
   * @remarks Any notes entered into the Scheduled Callback
   */
  lname: string;
  /**
   * @remarks Phone number to be dialed by Agent
   */
  phone: string;
  /**
   * @remarks time Zone
   */
  timeZone: string;
  /**
   * @remarks ISO 8601 time requested for Scheduled Callback
   */
  dateTime: string;
  /**
   * @remarks Any notes entered into the Scheduled Callback
   */
  notes: string;
  /**
   * @remarks "A" if targeted to an Agent or "S" if targeted to a skill
   */
  callbackId: string;
}

/**
 * @remarks "TimeZone" is for Timezone data
 */
export interface TimeZone {
  /**
   * @remarks display name for the timeZone
   */
  displayName: string;
  /**
   * @remarks time zone offset
   */
  offset: string;
  /**
   * @remarks standard name for time Zone
   */
  standardName: string;
}

/**
 * @remarks "CommitmentPermissions" is for commitment create/remove permission
 */
export interface CommitmentPermissions {
  /**
   * @remarks create commitment permission
   */
  create: boolean;
  /**
   * @remarks delete commitment permission
   */
  delete: boolean;
}

/**
 * @remarks "CommitmentState" is the redux state for add/delete/update commitment
 */
export interface CommitmentState {
  /**
   * @remarks flag to set the remove commitment toast messgae
   */
  isRemoveCommitmentToastMessage: boolean;
  /**
   * @remarks flag to show the commitment form through schedule app
   */
  showCommitmentForm: boolean;
  /**
   * @remarks flag to show the commitment form through call
   */
  addCommitmentByCall: boolean;
  /**
   * @remarks flag to edit the commitment form
   */
  isEditCommitment: boolean;
  /**
   * @remarks flag to reschedule the commitment form
   */
  isRescheduleCommitment: boolean;
  /**
   * @remarks commitment form data
   */
  formData: CommitmentFormState;
  /**
   * @remarks Array containing all the time Zones
   */
  timeZones: TimeZone[];
  /**
   * @remarks Array containing commitment details
   */
  commitment: CommitmentResponse[];

  /**
   * @remarks notes required  commitment permission
   */
  notesRequiredToDelete: boolean;

  /**
   * @remarks  commitment permissions
   */
  commitmentPermissions: CommitmentPermissions;

  /**
   * @remarks  commitment data to show commitment reminder
   */
  scheduledCommitment: ScheduledCommitment | null;
}

const initialFormState: CommitmentFormState = {
  commitmentType: '',
  skillId: '',
  fname: '',
  lname: '',
  phone: '',
  timeZone: getUtcTimezoneOffset(),
  dateTime: '',
  notes: '',
  callbackId: '',
};

export const commitmentState: CommitmentState = {
  isRemoveCommitmentToastMessage: false,
  showCommitmentForm: false,
  addCommitmentByCall: false,
  isEditCommitment: false,
  isRescheduleCommitment: false,
  timeZones: [],
  formData: initialFormState,
  commitment: [],
  notesRequiredToDelete: false,
  commitmentPermissions: {
    create: false,
    delete: false,
  },
  scheduledCommitment: null,
};

// /**
//  * Thunk action creator to fetch timeZone Data
//  * @returns - timeZone Array
//  * @example - dispatch(fetchTimeZones());
//  */
// export const fetchTimeZones = createAsyncThunk(FETCH_TIME_ZONES, () =>
//   CXoneClient.instance.common
//     .getTimezones()
//     .then((resp : any) => {
//       return resp;
//     })
//     .catch((error: any) => {
//       logger.debug(
//         '[CcfCommitmentSlice][fetchTimeZones]',
//         `payload: ${JSON.stringify(error)}`
//       );
//       return error;
//     })
// );

/**
 * Thunk action creator to fetch all the commitments for an agent
 * @param agentId - agent id
 * @returns - commitments[]
 * @example - dispatch(fetchCommitments('453678'));
 */
export const fetchCommitments = createAsyncThunk(
  FETCH_COMMITMENT,
  (agentId: number) =>
    CXoneClient.instance.commitment
      .getCommitments(agentId)
      .then((response: CommitmentResponse[]) => {
        return Array.isArray(response) ? response : [];
      })
      .catch((error: any) => {
        logger.debug(
          '[CcfCommitmentSlice][fetchCommitment]',
          `payload: ${JSON.stringify(error)}`
        );
        return error;
      })
);

// /**
//  * Thunk action creator to create an commitment
//  * @param commitmentPayload - commitment form data as payload
//  * @returns - callbackId
//  * @example - dispatch(createCommitment(payload));
//  */
// export const createCommitment = createAsyncThunk(
//   CREATE_COMMITMENT,
//   (commitmentPayload: CommitmentRequest, thunkAPI) =>
//     CXoneClient.instance.commitment
//       .createCommitment(commitmentPayload)
//       .catch((error: any) => {
//         logger.debug(
//           '[CcfCommitmentSlice][createCommitment]',
//           `payload: ${JSON.stringify(error)}`
//         );
//         return error;
//       })
// );

// /**
//  * Thunk action creator to edit a commitment
//  * @param commitmentPayload - commitment form data as payload
//  * @param callbackId - callbackId
//  * @returns - callbackId
//  * @example - dispatch(editCommitment(payload));
//  */
// export const editCommitment = createAsyncThunk(
//   EDIT_COMMITMENT,
//   (
//     data: { callbackId: number; commitmentPayload: CommitmentRequest },
//     thunkAPI
//   ) =>
//     CXoneClient.instance.commitment
//       .editCommitment(data.callbackId, data.commitmentPayload)
//       .catch((error : any) => {
//         logger.debug(
//           '[CcfCommitmentSlice][editCommitment]',
//           `payload: ${JSON.stringify(error)}`
//         );
//         return error;
//       })
// );

// /**
//  * Thunk action creator to handle delete commitment
//  * @param callbackId - callbackid and description
//  * @returns - callbackId
//  * @example - dispatch(deleteCommitment());
//  */
// export const deleteCommitment = createAsyncThunk(
//   DELETE_COMMITMENT,
//   (data: { callbackId: number; description: string }) =>
//     CXoneClient.instance.commitment
//       .deleteCommitment(data.callbackId, data.description)
//       .then(() => {
//         return { callbackId: data.callbackId };
//       })
//       .catch((error : any) => {
//         logger.debug(
//           '[CcfCommitmentSlice][deleteCommitment]',
//           `payload: ${JSON.stringify(error)}`
//         );
//         return error;
//       })
// );

// /**
//  * Thunk action creator to make a call for received commitment
//  * @param callbackId - callbackId
//  * @example - dispatch(makeCommitmentCall(callbackId));
//  */
// export const makeCommitmentCall = createAsyncThunk(
//   MAKE_COMMITMENT_CALL,
//   (callbackId: number) =>
//     CXoneClient.instance.commitment
//       .makeCommitmentCall(callbackId)
//       .then(() => {
//         return { callbackId };
//       })
//       .catch((error: any) => {
//         logger.debug(
//           '[CcfCommitmentSlice][makeCommitmentCall]',
//           `payload: ${JSON.stringify(error)}`
//         );
//         return error;
//       })
// );

// /**
//  * Thunk action creator to reschedule commitment
//  * @param callbackId - callbackId
//  * @param rescheduleDate - date of reschedule
//  * @example - dispatch(rescheduleCommitment(callbackId, rescheduleDate));
//  */
// export const rescheduleCommitment = createAsyncThunk(
//   RESCHEDULE_COMMITMENT,
//   (data: { callbackId: number; rescheduleDate: string }) =>
//     CXoneClient.instance.commitment
//       .rescheduleCommitment(data.callbackId, data.rescheduleDate)
//       .then(() => {
//         return { callbackId: data.callbackId };
//       })
//       .catch((error: any) => {
//         logger.debug(
//           '[CcfCommitmentSlice][rescheduleCommitment]',
//           `payload: ${JSON.stringify(error)}`
//         );
//         return error;
//       })
// );

// /**
//  * Thunk action creator to cancel commitment
//  * @param callbackId - callbackId
//  * @param description - short description
//  * @example - dispatch(cancelCommitment(callbackId, ""));
//  */
// export const cancelCommitment = createAsyncThunk(
//   CANCEL_COMMITMENT,
//   (data: { callbackId: number; description: string }) =>
//     CXoneClient.instance.commitment
//       .cancelCommitment(data.callbackId, data.description)
//       .then(() => {
//         return { callbackId: data.callbackId };
//       })
//       .catch((error: any) => {
//         logger.debug(
//           '[CcfCommitmentSlice][cancelCommitment]',
//           `payload: ${JSON.stringify(error)}`
//         );
//         return error;
//       })
// );

// /**
//  * Thunk action creator to check create commitment permission
//  * @returns - create commitment permission
//  * @example - dispatch(checkCreateCommitmentPermission());
//  */
// export const checkCreateCommitmentPermission = createAsyncThunk(
//   CHECK_CREATE_COMMITMENT_PERMISSION,
//   () =>
//     CXoneClient.instance.agentPermission
//       .checkPermissions(
//         PermissionKeys.HIDE_CREATE_COMMITMENTS,
//         PermissionValues.HIDE
//       )
//       .then((result : any) => {
//         return !result;
//       })
//       .catch((error : any) => {
//         logger.debug(
//           '[CcfCommitmentSlice][checkCreateCommitmentPermission]',
//           `payload: ${JSON.stringify(error)}`
//         );
//         return false;
//       })
// );

// /**
//  * Thunk action creator to get remove commitment settings
//  * @returns - remove commitment settings
//  * @example - dispatch(getRemoveCommitmentAgentSettings());
//  */
// export const getRemoveCommitmentAgentSettings = createAsyncThunk(
//   REMOVE_COMMITMENT_SETTINGS,
//   () =>
//     CXoneClient.instance.agentSetting
//       .getAgentSettings()
//       .then((result : any) => {
//         const agentSettings = result as AgentSettings;
//         const deleteCommitmentString = agentSettings.deleteCommitmentString;

//         let canDelete = true;
//         let notesRequiredToDelete = false;

//         switch (deleteCommitmentString) {
//         case 'CantRemove':
//           canDelete = false;
//           break;

//         case 'CanRemoveWithNotes':
//           notesRequiredToDelete = true;
//           break;

//         default:
//           canDelete = true;
//           notesRequiredToDelete = false;
//         }

//         return { canDelete, notesRequiredToDelete };
//       })
//       .catch((error: any) => {
//         logger.debug(
//           '[CcfCommitmentSlice][getCommitmentSettings]',
//           `payload: ${JSON.stringify(error)}`
//         );
//         return { canDelete: false, notesRequiredToDelete: false };
//       })
// );

export const commitmentSlice = createSlice({
  name: CCF_COMMITMENT_FEATURE_KEY,
  initialState: commitmentState,
  reducers: {
    /**
     * Function to set the value for showCommitmentForm
     * @param state - CommitmentState
     * @param action  - PayloadAction<boolean>
     * @returns It returns current value for showCommitmentForm
     * @example -showCommitmentForm(state,action)
     */
    showCommitmentForm(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        showCommitmentForm: action.payload,
      };
    },
    /**
     * Function to set the value for add commitment link from kebab menu
     * @param state - CommitmentState
     * @param action  - PayloadAction<boolean>
     * @returns It returns current value to open modal
     * @example -addCommitmentEvent(state,action)
     */
    addCommitmentEvent(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        addCommitmentByCall: action.payload,
      };
    },
    /**
     * Function to go back to the previous state
     * @param state - CommitmentState
     * @param action  - PayloadAction<boolean>
     * @returns It returns current value to open modal
     * @example -goBackToPreviousState(state,action)
     */
    goBackToPreviousState(state) {
      return {
        ...state,
        showCommitmentForm: false,
        addCommitmentByCall: false,
        formData: initialFormState,
      };
    },
    /**
     * Function to set form data
     * @param state - CommitmentState
     * @param action  - any
     * @returns It returns current value to open modal
     * @example -formData(state,action)
     */
    formData(
      state,
      action: PayloadAction<{ fieldName: string; fieldValue: string }>
    ) {
      return {
        ...state,
        formData: {
          ...state.formData,
          [action.payload.fieldName]: action.payload.fieldValue,
        },
      };
    },
    /**
     * Function to set the value for edit commitments prefill data
     * @param state - CommitmentState
     * @param action  - PayloadAction<EditCommitmentEvent>
     * @returns It returns the commitment data for edit form
     * @example -EditCommitmentEvent(state,action)
     */
    editCommitmentEvent(state, action: PayloadAction<CommitmentFormState>) {
      return {
        ...state,
        formData: { ...action.payload },
      };
    },
    /**
     * Function to set the value for edit commitments prefill data
     * @param state - CommitmentState
     * @param action  - PayloadAction<Boolean>
     * @returns It returns the commitment data for edit form
     * @example -setIsEditCommitment(state,action)
     */
    setIsEditCommitment(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isEditCommitment: action.payload,
      };
    },
    /**
     * Function to set pointer Events when toast appear
     * @param state - CommitmentState
     * @param action - PayloadAction<boolean>
     * @example - dispatch(removeCommitmentMessageConfirmed(true));
     * @returns - returns boolean value for remove confirmation toast
     */
    removeCommitmentMessageConfirmed(state, action: PayloadAction<boolean>) {
      state.isRemoveCommitmentToastMessage = action.payload;
      return state;
    },
    /**
     * Function to set scheduled commitemnt data to show in UI
     * @param state - CommitmentState
     * @param action - PayloadAction<CommitmentEvent>
     * @example - dispatch(setScheduledCommitment(commitmentEvent));
     * @returns - returns commitment state with scheduled commitment
     */
    setScheduledCommitment(
      state,
      action: PayloadAction<CommitmentEvent | null>
    ) {
      const scheduledCommitment = action.payload;
      const scheduledContact = scheduledCommitment
        ? {
          contactId: Number(scheduledCommitment.callbackId),
          media: MediaType.VOICE,
          isOutbound: true,
          isSelected: true,
          contactMode: scheduledCommitment.dialNumber,
          customerName: `${scheduledCommitment.firstName} ${scheduledCommitment.lastName}`,
          contactStatus: VoiceContactStatus.RINGING,
          skillOrQueueId: scheduledCommitment.skillId,
          contactReceivedTime: scheduledCommitment.deliveryTime,
          refusalTimeOut: 120000,
          isCommitmentReminder: true,
          notes: scheduledCommitment.notes,
          targetType: scheduledCommitment.targetType,
          firstName:  scheduledCommitment.firstName,
          lastName:  scheduledCommitment.lastName,
        }
        : null;
      state.scheduledCommitment = scheduledContact as ScheduledCommitment;
      return state;
    },
    /**
     * Function to set the isRescheduleCommitment flag
     * @param state - CommitmentState
     * @param action  - PayloadAction<Boolean>
     * @returns It returns the commitment state
     * @example - dispatch(setIsRescheduleCommitment(true))
     */
    setIsRescheduleCommitment(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isRescheduleCommitment: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    // builder.addCase(createCommitment.fulfilled, (state: CommitmentState) => {
    //   return {
    //     ...state,
    //     formData: initialFormState,
    //   };
    // });
    builder.addCase(
      fetchCommitments.fulfilled,
      (state: CommitmentState, action) => {
        return {
          ...state,
          commitment: [...action.payload],
        };
      }
    );
    // builder.addCase(
    //   deleteCommitment.fulfilled,
    //   (state: CommitmentState, action) => {
    //     return {
    //       ...state,
    //       commitment: state.commitment.filter(
    //         (item) =>
    //           Number(item.callbackId) !== Number(action.payload.callbackId)
    //       ),
    //     };
    //   }
    // );
    // builder.addCase(
    //   checkCreateCommitmentPermission.fulfilled,
    //   (state: CommitmentState, action) => {
    //     return {
    //       ...state,
    //       commitmentPermissions: {
    //         ...state.commitmentPermissions,
    //         create: action.payload,
    //       },
    //     };
    //   }
    // );
    // builder.addCase(
    //   getRemoveCommitmentAgentSettings.fulfilled,
    //   (state: CommitmentState, action) => {
    //     return {
    //       ...state,
    //       commitmentPermissions: {
    //         ...state.commitmentPermissions,
    //         delete: action.payload.canDelete,
    //       },
    //       notesRequiredToDelete: action.payload.notesRequiredToDelete,
    //     };
    //   }
    // );
  },
});

export const commitmentReducer = commitmentSlice.reducer;
export const commitmentActions = commitmentSlice.actions;

/**
 * Function to get commitment data
 * @param rootState - commitmentState
 * @example
 * @returns It returns commitment data
 */
export const getCommitmentState = (rootState: {
  ccfCommitment: CommitmentState;
}) => {
  return rootState[CCF_COMMITMENT_FEATURE_KEY];
};

export const getFormData = createSelector(
  getCommitmentState,
  (state : any) => state?.formData
);

export const getShowCommitmentFormState = createSelector(
  getCommitmentState,
  (state) => state?.showCommitmentForm
);

export const getAddCommitmentByCall = createSelector(
  getCommitmentState,
  (state) => state?.addCommitmentByCall
);

export const getTimeZones = createSelector(
  getCommitmentState,
  (state) => state?.timeZones
);

export const getIsEditCommitment = createSelector(
  getCommitmentState,
  (state) => state?.isEditCommitment
);

export const getCommitmentsList = createSelector(getCommitmentState, (state) =>
  state?.commitment.filter((commitment) => commitment.target === 'A')
);

export const isRemoveCommitmentToastMessage = createSelector(
  getCommitmentState,
  (state) => state.isRemoveCommitmentToastMessage
);

export const getRemoveCommitmentSettings = createSelector(
  getCommitmentState,
  (state) => state.notesRequiredToDelete
);

export const getCommitmentPermission = createSelector(
  getCommitmentState,
  (state) => state?.commitmentPermissions
);

export const getScheduledCommitment = createSelector(
  getCommitmentState,
  (state) => state?.scheduledCommitment
);

export const getIsRescheduleCommitment = createSelector(
  getCommitmentState,
  (state) => state?.isRescheduleCommitment
);
